import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import Button from "../common/Button";
import Modal from "../common/Modal";
import Switch from "../common/Switch";
import Loader from "../common/Loader";
import { getEventQRBufferTime, updateEventQRBufferTime } from "../../services/eventDetailService";
import { color } from "../../styles/theme";

const DEFAULT_BEFORE_HOURS = 2;
const DEFAULT_AFTER_HOURS = 2;
const DEFAULT_IS_ACTIVE = true;
const HOUR_OPTIONS = Array.from({ length: 25 }, (_, i) => i);

/**
 * Response field names for GetEventEQRTime aren't 100%-confirmed even in
 * mobile's own source — it defensively tries several casings/nesting paths.
 * Ported as-is rather than assuming one shape.
 */
const getQRTimeValue = (response, key) =>
  response?.[key] ??
  response?.EventEQRTime?.[key] ??
  response?.Data?.[key] ??
  response?.data?.[key] ??
  response?.Result?.[key] ??
  response?.[`${key.charAt(0).toLowerCase()}${key.slice(1)}`];

const getBooleanQRTimeValue = (response, keys) => {
  for (const key of keys) {
    const value = getQRTimeValue(response, key);
    if (value !== undefined && value !== null) {
      return value === true || value === "true" || value === 1 || value === "1";
    }
  }
  return undefined;
};

/**
 * eQR verification buffer time — ported from mobile's event-qr-buffer-time
 * screen (src/Screens/event-qr-buffer-time). Opened from the "QR Buffer
 * Time" tile on DetailsTab.jsx instead of a separate route.
 */
const QRBufferTimeModal = ({ eventId, onClose, onUpdated }) => {
  const { t } = useTranslation(["eventDetails", "common"]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [before, setBefore] = useState(DEFAULT_BEFORE_HOURS);
  const [after, setAfter] = useState(DEFAULT_AFTER_HOURS);
  const [gpsEnabled, setGpsEnabled] = useState(DEFAULT_IS_ACTIVE);
  const [initial, setInitial] = useState({ before: DEFAULT_BEFORE_HOURS, after: DEFAULT_AFTER_HOURS, gpsEnabled: DEFAULT_IS_ACTIVE });

  useEffect(() => {
    getEventQRBufferTime(eventId)
      .then((res) => {
        const response = res.data;
        const beforeHours = getQRTimeValue(response, "ValidBeforeEventStartHours");
        const afterHours = getQRTimeValue(response, "ValidAfterEventEndHours");
        const gps = getBooleanQRTimeValue(response, [
          "IsActive", "IsGpsVerificationEnabled", "IsGPSVerificationEnabled",
          "GpsVerificationEnabled", "GPSVerificationEnabled", "EnableGpsVerification", "EnableGPSVerification",
        ]);
        const nextBefore = beforeHours != null ? Number(beforeHours) : DEFAULT_BEFORE_HOURS;
        const nextAfter = afterHours != null ? Number(afterHours) : DEFAULT_AFTER_HOURS;
        const nextGps = gps !== undefined ? gps : DEFAULT_IS_ACTIVE;
        setBefore(nextBefore);
        setAfter(nextAfter);
        setGpsEnabled(nextGps);
        setInitial({ before: nextBefore, after: nextAfter, gpsEnabled: nextGps });
      })
      .catch(() => toast.error(t("qrBufferTimeModal.loadError")))
      .finally(() => setLoading(false));
  }, [eventId]);

  const hasChanges = useMemo(
    () => before !== initial.before || after !== initial.after || gpsEnabled !== initial.gpsEnabled,
    [before, after, gpsEnabled, initial]
  );

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await updateEventQRBufferTime({
        EventId: String(eventId),
        ValidBeforeEventStartHours: Number(before),
        ValidAfterEventEndHours: Number(after),
        IsActive: gpsEnabled,
      });
      toast.success(res.data?.Message || t("qrBufferTimeModal.updateSuccess"));
      onUpdated();
    } catch (err) {
      toast.error(err.response?.data?.Message || t("qrBufferTimeModal.updateError"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      title={t("qrBufferTimeModal.title")}
      subtitle={t("qrBufferTimeModal.subtitle")}
      onClose={onClose}
      busy={saving}
      maxWidth={400}
      footer={!loading && (
        <>
          <Button variant="secondary" onClick={onClose} disabled={saving}>{t("common:actions.cancel")}</Button>
          <Button onClick={handleSave} disabled={saving || !hasChanges}>{saving ? t("qrBufferTimeModal.saving") : t("common:actions.save")}</Button>
        </>
      )}
    >
      {loading ? (
        <Loader />
      ) : (
        <>
          <div style={{ marginBottom: 14 }}>
            <label style={s.fieldLabel}>{t("qrBufferTimeModal.beforeEventStart")}</label>
            <select className="input" style={s.select} value={before} onChange={(e) => setBefore(Number(e.target.value))} disabled={saving}>
              {HOUR_OPTIONS.map((h) => <option key={h} value={h}>{t("qrBufferTimeModal.hourOption", { count: h })}</option>)}
            </select>
          </div>

          <div style={{ marginBottom: 6 }}>
            <label style={s.fieldLabel}>{t("qrBufferTimeModal.afterEventEnd")}</label>
            <select className="input" style={s.select} value={after} onChange={(e) => setAfter(Number(e.target.value))} disabled={saving}>
              {HOUR_OPTIONS.map((h) => <option key={h} value={h}>{t("qrBufferTimeModal.hourOption", { count: h })}</option>)}
            </select>
          </div>

          <div style={s.switchRow}>
            <span style={s.fieldLabel}>{t("qrBufferTimeModal.enableGpsVerification")}</span>
            <Switch checked={gpsEnabled} onChange={(e) => setGpsEnabled(e.target.checked)} disabled={saving} />
          </div>
        </>
      )}
    </Modal>
  );
};

const s = {
  fieldLabel: { display: "block", marginBottom: 7, fontWeight: 600, fontSize: 13, color: color.gray700 },
  select: { cursor: "pointer" },
  switchRow: { display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 2px 4px", borderTop: `1px solid ${color.gray100}`, marginTop: 8 },
};

export default QRBufferTimeModal;
